import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Button,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogContent,
  DialogTitle,
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import { Dismiss24Regular, ArrowClockwise24Regular } from '@fluentui/react-icons';
import DataTable, { ColumnConfig } from '../components/DataTable';
import { apiFetch } from '../lib/apiFetch';
import { formatBusinessDateTime } from '../lib/businessTime';

interface ContactSubmission {
  id: string;
  name: string;
  email: string;
  subject: string;
  message: string;
  source_ip: string | null;
  user_agent: string | null;
  created_at: string | null;
}

interface ListResponse {
  data: ContactSubmission[];
}

const useStyles = makeStyles({
  page: {
    paddingTop: '16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  toolbarRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    ...shorthands.padding('0', '16px'),
  },
  tableContainer: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: '4px',
    boxShadow: tokens.shadow8,
    overflow: 'hidden',
  },
  emptyState: {
    ...shorthands.padding('40px'),
    textAlign: 'center',
    color: tokens.colorNeutralForeground3,
  },
  messageSnippet: {
    color: tokens.colorNeutralForeground2,
    maxWidth: '420px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    display: 'inline-block',
  },
  detailHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    ...shorthands.padding('16px', '24px', '8px'),
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke2),
  },
  detailBody: {
    ...shorthands.padding('16px', '24px', '24px'),
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  detailRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  detailLabel: {
    fontSize: '12px',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    color: tokens.colorNeutralForeground3,
  },
  detailValue: {
    fontSize: '14px',
    color: tokens.colorNeutralForeground1,
  },
  detailMessage: {
    fontSize: '14px',
    color: tokens.colorNeutralForeground1,
    whiteSpace: 'pre-wrap',
    lineHeight: 1.6,
  },
  errorBanner: {
    margin: '0 16px',
    padding: '12px',
    backgroundColor: tokens.colorPaletteRedBackground1,
    border: `1px solid ${tokens.colorPaletteRedBorder1}`,
    borderRadius: '4px',
    color: tokens.colorPaletteRedForeground1,
  },
});

function formatTimestamp(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return formatBusinessDateTime(iso);
}

const ContactSubmissions: React.FC = () => {
  const styles = useStyles();
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const { data, isLoading, error, refetch, isFetching } = useQuery<ListResponse>({
    queryKey: ['contact-submissions'],
    queryFn: async () => {
      const res = await apiFetch('/contact-submissions');
      if (!res.ok) throw new Error('Failed to load contact submissions');
      return res.json();
    },
  });

  const submissions = data?.data || [];
  const selected = submissions.find((s) => s.id === selectedId) || null;

  const columns: ColumnConfig<ContactSubmission>[] = [
    {
      columnKey: 'created_at',
      label: 'Received',
      render: (s) => formatTimestamp(s.created_at),
    },
    {
      columnKey: 'name',
      label: 'From',
      render: (s) => s.name,
    },
    {
      columnKey: 'email',
      label: 'Email',
      render: (s) => s.email,
    },
    {
      columnKey: 'subject',
      label: 'Subject',
      render: (s) => s.subject,
    },
    {
      columnKey: 'message',
      label: 'Message',
      render: (s) => (
        <span className={styles.messageSnippet} title={s.message}>
          {s.message}
        </span>
      ),
    },
  ];

  return (
    <div className={styles.page}>
      <div className={styles.toolbarRow}>
        <Button
          icon={<ArrowClockwise24Regular />}
          onClick={() => refetch()}
          disabled={isFetching}
          appearance="subtle"
        >
          Refresh
        </Button>
      </div>

      {error && (
        <div className={styles.errorBanner}>{(error as Error).message}</div>
      )}

      <div className={styles.tableContainer}>
        {isLoading && (
          <div className={styles.emptyState}>Loading submissions…</div>
        )}

        {!isLoading && submissions.length === 0 && !error && (
          <div className={styles.emptyState}>No contact-form submissions yet.</div>
        )}

        {!isLoading && submissions.length > 0 && (
          <DataTable
            data={submissions}
            columns={columns}
            keyField="id"
            hideableColumns
            columnVisibilityStorageKey="contact_submissions"
            itemsPerPage={50}
            showCheckboxes={false}
            primaryColumnKey="subject"
            onRowClick={(s) => setSelectedId(s.id)}
          />
        )}
      </div>

      <Dialog
        open={!!selectedId}
        onOpenChange={(_, { open }) => { if (!open) setSelectedId(null); }}
      >
        <DialogSurface style={{ maxWidth: '90vw', width: '640px', padding: 0 }}>
          <DialogBody style={{ display: 'flex', flexDirection: 'column' }}>
            <div className={styles.detailHeader}>
              <DialogTitle style={{ margin: 0, fontSize: '18px' }}>
                {selected?.subject || 'Contact Submission'}
              </DialogTitle>
              <Button
                appearance="subtle"
                icon={<Dismiss24Regular />}
                aria-label="Close"
                onClick={() => setSelectedId(null)}
              />
            </div>
            <DialogContent style={{ padding: 0 }}>
              {selected && (
                <div className={styles.detailBody}>
                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>From</span>
                    <span className={styles.detailValue}>
                      {selected.name} &lt;
                      <a href={`mailto:${selected.email}?subject=Re: ${encodeURIComponent(selected.subject)}`}>
                        {selected.email}
                      </a>
                      &gt;
                    </span>
                  </div>
                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>Received</span>
                    <span className={styles.detailValue}>{formatTimestamp(selected.created_at)}</span>
                  </div>
                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>Message</span>
                    <div className={styles.detailMessage}>{selected.message}</div>
                  </div>
                  {(selected.source_ip || selected.user_agent) && (
                    <div className={styles.detailRow}>
                      <span className={styles.detailLabel}>Origin</span>
                      <span className={styles.detailValue} style={{ fontFamily: 'monospace', fontSize: '12px' }}>
                        {selected.source_ip || '—'}
                      </span>
                      {selected.user_agent && (
                        <span className={styles.detailValue} style={{ fontFamily: 'monospace', fontSize: '12px', color: tokens.colorNeutralForeground3 }}>
                          {selected.user_agent}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              )}
            </DialogContent>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
};

export default ContactSubmissions;
